import { useState, useRef, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  MessageCircle, X, Send, Loader2, Bot, User, 
  AlertCircle, HelpCircle, Minimize2, Maximize2
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";

export default function AIChatWidget() {
  const { token, user, userType } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hi! I'm the TyreMatch assistant. I can help you with questions about our platform, getting quotes, or connecting with support. How can I help you today?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showEscalation, setShowEscalation] = useState(false);
  const messagesEndRef = useRef(null);

  // Escalation form state
  const [escalationForm, setEscalationForm] = useState({
    issue_category: "",
    message: "",
    related_quote_id: ""
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      
      const response = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      const data = await response.json();
      
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
      }

      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: data.response,
        error: data.error
      }]);

      // Check if AI suggests escalation
      if (data.response.toLowerCase().includes("support team") || 
          data.response.toLowerCase().includes("escalate") ||
          data.error) {
        // Show escalation option after a delay
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: "system",
            content: "Would you like to create a support ticket to speak with a human agent?",
            isEscalationPrompt: true
          }]);
        }, 1000);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I'm having trouble connecting right now. Would you like to create a support ticket instead?",
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitEscalation = async () => {
    if (!escalationForm.issue_category || !escalationForm.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/chat/escalate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_name: user?.name || "Unknown",
          email: user?.email || "",
          account_type: userType || "customer",
          issue_category: escalationForm.issue_category,
          message: escalationForm.message,
          related_quote_id: escalationForm.related_quote_id || null
        })
      });

      if (!response.ok) {
        throw new Error("Failed to create ticket");
      }

      const data = await response.json();
      
      toast.success("Support ticket created! We'll get back to you soon.");
      setShowEscalation(false);
      setEscalationForm({ issue_category: "", message: "", related_quote_id: "" });
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `I've created support ticket #${data.ticket_id.substring(0, 8)} for you. Our team will respond within 1-2 business days via email at ${user?.email}.`
      }]);
    } catch (error) {
      toast.error("Failed to create support ticket");
    } finally {
      setLoading(false);
    }
  };

  // Don't render if user is not logged in
  // Show on all pages (logged-in users get full features, anonymous get FAQ only)
  
  return (
    <>
      {/* Floating Chat Button — smaller + tucked further out on mobile so it
          doesn't cover Accept/CTA controls (Feb 2026 mobile-fix). */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-3 right-3 sm:bottom-6 sm:right-6 z-40 group"
          data-testid="chat-widget-button"
        >
          <div className="relative transition-transform group-hover:scale-110">
            {/* Chat bubble background */}
            <svg width="56" height="56" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-lg sm:w-[72px] sm:h-[72px]">
              <rect x="2" y="2" width="62" height="52" rx="14" fill="#f97316" />
              <path d="M12 54 L24 54 L12 68 Z" fill="#f97316" />
            </svg>
            {/* Robot mascot image */}
            <img
              src="/wingman-bot.png"
              alt="Chat"
              className="absolute top-[6px] left-[6px] w-[36px] h-[32px] sm:top-[8px] sm:left-[8px] sm:w-[46px] sm:h-[40px] rounded-lg object-cover"
            />
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={cn(
            "fixed z-40 bg-background border border-border rounded-xl shadow-2xl transition-all duration-300",
            isMinimized 
              ? "bottom-3 right-3 sm:bottom-6 sm:right-6 w-64 sm:w-72 h-14" 
              : "bottom-3 right-3 sm:bottom-6 sm:right-6 w-[calc(100vw-1.5rem)] sm:w-96 h-[70vh] sm:h-[500px] max-h-[80vh]"
          )}
          data-testid="chat-widget-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30 rounded-t-xl">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold">TyreMatch Assistant</p>
                {!isMinimized && (
                  <p className="text-xs text-muted-foreground">Powered by AI</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Chat Content */}
          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[360px]">
                {messages.map((message, index) => (
                  <div
                    key={`msg-${index}-${message.role}`}
                    className={cn(
                      "flex gap-2",
                      message.role === "user" ? "flex-row-reverse" : "flex-row"
                    )}
                  >
                    {message.role !== "system" && (
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                        message.role === "user" ? "bg-primary" : "bg-muted"
                      )}>
                        {message.role === "user" ? (
                          <User className="h-4 w-4 text-primary-foreground" />
                        ) : (
                          <Bot className="h-4 w-4" />
                        )}
                      </div>
                    )}
                    <div className={cn(
                      "max-w-[80%] rounded-lg px-3 py-2",
                      message.role === "user" 
                        ? "bg-primary text-primary-foreground" 
                        : message.role === "system"
                          ? "bg-amber-500/10 border border-amber-500/30 text-sm"
                          : "bg-muted"
                    )}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      {message.isEscalationPrompt && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          onClick={() => setShowEscalation(true)}
                        >
                          <HelpCircle className="h-3 w-3 mr-1" />
                          Create Support Ticket
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      <Bot className="h-4 w-4" />
                    </div>
                    <div className="bg-muted rounded-lg px-3 py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-border">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type your message..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button
                    size="icon"
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    className="bg-primary"
                    data-testid="chat-send-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <button
                  className="text-xs text-muted-foreground mt-2 hover:text-primary transition-colors"
                  onClick={() => setShowEscalation(true)}
                >
                  Need human support? Create a ticket
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Escalation Dialog */}
      <Dialog open={showEscalation} onOpenChange={setShowEscalation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogDescription>
              Our team will respond within 1-2 business days
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Issue Category *</Label>
              <Select
                value={escalationForm.issue_category}
                onValueChange={(value) => setEscalationForm(prev => ({ ...prev, issue_category: value }))}
              >
                <SelectTrigger data-testid="escalation-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quote_issue">Quote Issues</SelectItem>
                  <SelectItem value="payment_issue">Payment Problems</SelectItem>
                  <SelectItem value="shop_complaint">Shop Complaint</SelectItem>
                  <SelectItem value="account_issue">Account Issues</SelectItem>
                  <SelectItem value="technical_issue">Technical Problems</SelectItem>
                  <SelectItem value="general_inquiry">General Inquiry</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Related Quote/Order ID (Optional)</Label>
              <Input
                placeholder="e.g. ABC123"
                value={escalationForm.related_quote_id}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, related_quote_id: e.target.value }))}
                data-testid="escalation-quote-id"
              />
            </div>

            <div className="space-y-2">
              <Label>Describe Your Issue *</Label>
              <Textarea
                placeholder="Please provide details about your issue..."
                value={escalationForm.message}
                onChange={(e) => setEscalationForm(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
                data-testid="escalation-message"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEscalation(false)}>
                Cancel
              </Button>
              <Button
                onClick={submitEscalation}
                disabled={loading}
                className="bg-primary"
                data-testid="escalation-submit-btn"
              >
                {loading ? "Submitting..." : "Submit Ticket"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
